import {
  CalendarBlankIcon,
  CalendarDotsIcon,
  CalendarXIcon,
  CaretRightIcon,
  CheckCircleIcon,
  PlusIcon,
  ScissorsIcon,
  WalletIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import { type StatementUrgency, formatMoney } from '@paybae/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Card, EmptyState } from '@/components/Card'
import { ProgressRing } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Monto,
  type OpcionSegmento,
  Segmentos,
} from '@/components/Form'
import { Money } from '@/components/Money'
import { PersonDot } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import { ApiError, type CreditCard, type StatementSummary } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useAccounts, useCards, useMeses, usePagarTarjeta } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Tarjetas de crédito.
 *
 * Cada tarjeta responde tres preguntas en este orden, que es el de urgencia
 * real: cuánto debo pagar y cuándo, cuánto crédito me queda, y cuándo vuelve a
 * cortar. La fecha límite se calcula con días hábiles y festivos bancarios
 * mexicanos, no con días naturales — la diferencia son varios días y un recargo.
 *
 * Ese cálculo lo hace el servidor y llega resuelto en cada estado de cuenta; la
 * pantalla no recalcula ciclos. Recalcularlos aquí abriría la puerta a que el
 * aviso de la app y el corte real del banco discrepen por un día.
 *
 * Desde aquí salen las tres acciones que la tarjeta admite: darla de alta,
 * corregir sus especificaciones y registrar el pago de un estado de cuenta.
 */

const URGENCIA: Record<
  StatementUrgency,
  { etiqueta: string; clase: string; Icon: typeof WarningIcon }
> = {
  OVERDUE: { etiqueta: 'Vencida', clase: 'text-expense-text', Icon: WarningIcon },
  DUE_TODAY: { etiqueta: 'Vence hoy', clase: 'text-expense-text', Icon: WarningIcon },
  DUE_SOON: { etiqueta: 'Por vencer', clase: 'text-expense-text', Icon: CalendarXIcon },
  // «En plazo», no «Al corriente»: hay un pago pendiente y decir lo contrario
  // invitaría a ignorarlo. El estado describe el tiempo que queda, no que no se
  // deba nada.
  SCHEDULED: { etiqueta: 'En plazo', clase: 'text-ink-soft', Icon: CheckCircleIcon },
}

export function Cards() {
  const navigate = useNavigate()
  const { membership } = useSession()
  const { data, isPending, isError, error, refetch } = useCards()
  // Las compras a meses se piden una sola vez para toda la lista y se reparten
  // por tarjeta: una consulta por tarjeta multiplicaría la red por nada, porque
  // el endpoint del hogar ya las trae todas con su tarjeta dentro.
  const compras = useMeses()

  // Alta y edición son de administración; registrar un pago no —cada quien paga
  // su tarjeta—. Ofrecer un botón que el servidor va a rechazar con un 403 es
  // peor que no ofrecerlo.
  const puedeAdministrar = membership.role === 'ADMIN'

  /** Tarjeta cuyo pago se está registrando. La hoja se abre encima, sin ruta. */
  const [pagando, setPagando] = useState<CreditCard | null>(null)

  if (isPending) return <Cargando />

  if (isError || !data) {
    return (
      <>
        <ScreenHeader title="Tarjetas" tinte="lavender" />
        <ErrorState
          titulo="No pudimos cargar tus tarjetas"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      </>
    )
  }

  if (data.length === 0) {
    return (
      <>
        <ScreenHeader title="Tarjetas" subtitle="Sin tarjetas todavía" tinte="lavender" />
        <div className="px-safe mx-auto flex max-w-lg flex-col gap-3 pt-1">
          <Card>
            <EmptyState
              title="Aún no registras ninguna tarjeta"
              description="Al añadir una, PayBae calcula solo el corte, la fecha límite en días hábiles y cuánto pagar para no generar intereses."
              action={
                puedeAdministrar ? (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('light')
                      navigate('/tarjetas/nueva')
                    }}
                    className="flex min-h-[44px] items-center gap-2 rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                  >
                    <PlusIcon size={16} weight="bold" />
                    Añadir tarjeta
                  </button>
                ) : (
                  <p className="text-[13px] text-ink-soft">
                    Pídele a quien administra el hogar que dé de alta la primera.
                  </p>
                )
              }
            />
          </Card>
        </div>
      </>
    )
  }

  const deudaTotal = data.reduce((s, c) => s + c.currentBalanceCents, 0)

  return (
    <>
      <ScreenHeader
        title="Tarjetas"
        subtitle={`${formatMoney(deudaTotal, { hideZeroCents: true })} en total`}
        tinte="lavender"
        action={
          <div className="flex shrink-0 items-center gap-2">
            {/* Las compras a meses colgaban solo de su propio formulario: se
                podía entrar a crear una, pero no había forma de volver a
                verlas. Una función entera invisible desde la pantalla donde
                todo el mundo la buscaría. */}
            <button
              type="button"
              onClick={() => {
                haptic('light')
                navigate('/tarjetas/meses')
              }}
              aria-label="Ver tus compras a meses"
              className="flex min-h-[40px] items-center gap-1.5 rounded-pill border border-line bg-surface px-3 text-[13px] font-semibold text-ink active:scale-95"
            >
              <CalendarBlankIcon size={15} weight="bold" />
              A meses
            </button>
            {puedeAdministrar ? <BotonAnadir onClick={() => navigate('/tarjetas/nueva')} /> : null}
          </div>
        }
      />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-3 pt-1">
        {data.map((card) => {
          const activos = (compras.data?.plans ?? []).filter(
            (p) => p.creditCardId === card.id && p.status === 'ACTIVE',
          )
          return (
            <TarjetaFila
              key={card.id}
              card={card}
              editable={puedeAdministrar}
              meses={{
                cuantas: activos.length,
                // La misma regla que usa el servidor para lo comprometido: la
                // **siguiente** mensualidad de cada plan vivo, no la suma de
                // todas ni el precio de la compra.
                alMesCents: activos.reduce((s, p) => s + (p.nextInstallment?.amountCents ?? 0), 0),
              }}
              onVerMeses={() => navigate(`/tarjetas/meses?tarjeta=${card.id}`)}
              onEditar={() => navigate(`/tarjetas/${card.id}/editar`)}
              onPagar={() => setPagando(card)}
            />
          )
        })}

        {/* El acceso también abajo, no solo en el encabezado: al final de una
            lista de varias tarjetas el botón de arriba ya quedó fuera de vista,
            y es justo ahí donde aparece la intención de añadir otra. */}
        {puedeAdministrar ? (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              navigate('/tarjetas/nueva')
            }}
            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-card border border-line bg-surface text-[15px] font-semibold text-ink active:scale-[0.98]"
          >
            <PlusIcon size={18} weight="bold" />
            Añadir tarjeta
          </button>
        ) : null}

        <Card className="bg-surface-sunken">
          <p className="text-[13px] leading-snug text-ink-soft">
            Las fechas límite se calculan en <strong className="text-ink">días hábiles</strong>,
            saltando fines de semana y días inhábiles bancarios (incluidos Jueves y Viernes
            Santo). Puedes ajustar el plazo y el calendario de cada tarjeta tocándola.
          </p>
        </Card>
      </div>

      {pagando ? <HojaPago card={pagando} onCerrar={() => setPagando(null)} /> : null}
    </>
  )
}

function BotonAnadir({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      aria-label="Añadir tarjeta"
      className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill bg-accion px-3.5 text-[14px] font-semibold text-on-accion active:scale-95"
    >
      <PlusIcon size={16} weight="bold" />
      Añadir
    </button>
  )
}

function TarjetaFila({
  card,
  editable,
  meses,
  onVerMeses,
  onEditar,
  onPagar,
}: {
  card: CreditCard
  editable: boolean
  meses: { cuantas: number; alMesCents: number }
  onVerMeses: () => void
  onEditar: () => void
  onPagar: () => void
}) {
  // `closedStatement` es el ciclo que YA cortó: de ahí sale lo que hay que pagar
  // y la cuenta regresiva. `openStatement` es el ciclo en curso, que vence a mes
  // y medio; solo aporta la fecha del próximo corte. Tomar uno por el otro
  // anunciaría el vencimiento con un mes de retraso.
  const cerrado = card.closedStatement
  const enCurso = card.openStatement

  const owner = card.owner
  // El token de color viene del servidor y es lo único que puede entrar aquí: un
  // id de integrante también es cadena, compila igual y pintaría a todos del
  // mismo color por caer en el color de reserva.
  const palette = personPalette(owner?.personColorToken)
  const estado = cerrado ? URGENCIA[cerrado.countdown.urgency] : null

  const cuerpo = (
    <>
      <header className="flex items-start gap-3">
        {/* El anillo lleva el uso de la línea: misma forma que la dona del
            inicio, así ambas se leen con el mismo gesto mental. */}
        <ProgressRing
          percent={card.utilizationPct}
          size={52}
          stroke={6}
          // El coral solo a partir del 80%: por debajo, un uso alto es normal y
          // teñirlo de alarma entrenaría a ignorar el color.
          color={card.utilizationPct >= 80 ? 'var(--expense)' : palette.cssVar}
        >
          <span data-money className="text-[12px] font-bold text-ink">
            {Math.round(card.utilizationPct)}
          </span>
        </ProgressRing>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-[15px] font-semibold text-ink">
            {owner ? <PersonDot token={owner.personColorToken} /> : null}
            <span className="truncate">{card.name}</span>
            {card.last4 ? (
              <span className="font-mono text-[12px] font-normal text-ink-faint">
                ·· {card.last4}
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 text-[12px] text-ink-soft">
            {[card.issuer, owner ? owner.displayName.split(' ')[0] : 'Común']
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p data-money className="mt-1 text-[12px] text-ink-faint">
            {formatMoney(card.availableCreditCents, { hideZeroCents: true })} disponibles de{' '}
            {formatMoney(card.creditLimitCents, { hideZeroCents: true })}
          </p>
          {/* Por qué la línea bajó sin que haya un movimiento que lo explique.
              El capital de una compra a meses no tiene movimiento detrás —se
              deriva del plan—, así que sin esta línea la resta parece un error
              de la app: el banco reserva los $15,666 de una compra a doce meses
              el día de la compra, no $1,305 al mes. */}
          {card.balanceBreakdown.unbilledInstallmentsCents > 0 ? (
            <p data-money className="mt-0.5 text-[12px] text-ink-faint">
              incluye{' '}
              {formatMoney(card.balanceBreakdown.unbilledInstallmentsCents, {
                hideZeroCents: true,
              })}{' '}
              de compras a meses por facturar
            </p>
          ) : null}
        </div>

        {estado ? (
          <span className={cn('flex shrink-0 items-center gap-1 text-[12px] font-medium', estado.clase)}>
            <estado.Icon size={14} weight="fill" />
            {estado.etiqueta}
          </span>
        ) : null}
      </header>

      {/* Lo que se debe pagar, primero: es la única cifra con consecuencia. */}
      <div className="mt-4 rounded-card bg-surface-sunken p-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] text-ink-soft">Pago para no generar intereses</span>
          <Money cents={cerrado?.balanceDueCents ?? 0} className="text-lg" />
        </div>

        {/* De qué está hecho el corte.
            Consumo y mensualidades no son lo mismo y por eso van separados: el
            consumo del mes se puede dejar de gastar el mes que viene, la
            mensualidad de una compra a meses no —ya se compró—. Sumados en una
            sola cifra, esa diferencia desaparece justo cuando se está decidiendo
            dónde recortar. El servidor los devuelve aparte; aquí solo se
            respeta esa separación. */}
        {cerrado && cerrado.installmentsCents > 0 ? (
          <dl className="mt-2.5 flex flex-col gap-1 border-t border-line pt-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[12px] text-ink-soft">Consumo del corte</dt>
              <dd data-money className="font-mono text-[12px] text-ink">
                {formatMoney(cerrado.chargesCents, { hideZeroCents: true })}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="flex items-center gap-1.5 text-[12px] text-ink-soft">
                <CalendarDotsIcon size={13} weight="fill" className="text-ink-faint" />
                Mensualidades a meses
              </dt>
              <dd data-money className="font-mono text-[12px] font-semibold text-ink">
                {formatMoney(cerrado.installmentsCents, { hideZeroCents: true })}
              </dd>
            </div>
            <p className="mt-0.5 text-[11px] leading-snug text-ink-faint">
              Lo primero puedes dejar de gastarlo el mes que viene; lo segundo ya no.
            </p>
          </dl>
        ) : null}

        {cerrado ? (
          <p className="mt-1.5 text-[12px] text-ink-soft">
            Límite: <span className="font-mono text-ink">{formatoLargo(cerrado.dueDate)}</span>
            {cerrado.countdown.calendarDaysUntilDue >= 0 ? (
              <>
                {' · '}
                {cerrado.countdown.calendarDaysUntilDue === 0
                  ? 'hoy'
                  : `en ${cerrado.countdown.calendarDaysUntilDue} días`}{' '}
                <span className="text-ink-faint">
                  ({cerrado.countdown.businessDaysUntilDue} hábiles)
                </span>
              </>
            ) : (
              <span className="text-expense-text">
                {' '}
                · vencida hace {-cerrado.countdown.calendarDaysUntilDue} días
              </span>
            )}
          </p>
        ) : (
          // Tarjeta recién dada de alta: todavía no ha cortado, así que no hay
          // fecha límite que anunciar. Decirlo es más útil que dejar el hueco.
          <p className="mt-1.5 text-[12px] text-ink-soft">
            Todavía no hay un corte cerrado en esta tarjeta.
          </p>
        )}
        {cerrado && cerrado.safePaymentDate !== cerrado.dueDate ? (
          // Cuando el límite cae en inhábil, pagar antes es la única opción
          // segura: una transferencia de fin de semana puede no acreditarse.
          <p className="mt-1 text-[12px] text-expense-text">
            El límite cae en día inhábil. Paga a más tardar el{' '}
            <span className="font-mono">{formatoLargo(cerrado.safePaymentDate)}</span>.
          </p>
        ) : null}
      </div>
    </>
  )

  return (
    <Card className="flex flex-col p-0">
      {/* Tocar la tarjeta abre sus especificaciones. El botón envuelve solo el
          cuerpo, nunca el pie: un botón dentro de otro no es HTML válido y en
          iOS el toque acaba activando el de fuera. */}
      {editable ? (
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onEditar()
          }}
          aria-label={`Editar ${card.name}`}
          className="w-full p-4 text-left active:scale-[0.99]"
        >
          {cuerpo}
        </button>
      ) : (
        <div className="p-4">{cuerpo}</div>
      )}

      {/* Acceso a las compras a meses de ESTA tarjeta.
          Va en el pie y no dentro del cuerpo porque el cuerpo entero es un
          botón cuando la tarjeta es editable, y un botón dentro de otro no es
          HTML válido: en iOS el toque acabaría abriendo el de fuera. */}
      <button
        type="button"
        onClick={() => {
          haptic('light')
          onVerMeses()
        }}
        className="flex min-h-[48px] w-full items-center gap-2.5 border-t border-line px-4 py-2.5 text-left active:opacity-70"
      >
        <CalendarDotsIcon
          size={17}
          weight="fill"
          className={meses.cuantas > 0 ? 'shrink-0 text-ink-soft' : 'shrink-0 text-ink-faint'}
        />
        <span className="min-w-0 flex-1 text-[13px] text-ink-soft">
          {meses.cuantas === 0 ? (
            'Sin compras a meses'
          ) : (
            <>
              <span className="font-semibold text-ink">
                {meses.cuantas} {meses.cuantas === 1 ? 'compra' : 'compras'} a meses
              </span>
              {' · '}
              <span data-money className="font-semibold text-ink">
                {formatMoney(meses.alMesCents, { hideZeroCents: true })}
              </span>{' '}
              al mes
              {/* Se nombra la acción en vez de dejarla a tres toques de
                  distancia detrás de un icono: liquidar una compra es de las
                  pocas cosas que se buscan a propósito, y quien la busca no
                  adivina que vive dentro de esta fila. */}
              <span className="mt-0.5 block text-[12px] text-ink-faint">
                Ver el calendario o liquidar una compra
              </span>
            </>
          )}
        </span>
        <CaretRightIcon size={14} weight="bold" className="shrink-0 text-ink-faint" />
      </button>

      <footer className="flex items-center gap-3 border-t border-line px-4 py-3">
        <p className="flex min-w-0 flex-1 items-center gap-1.5 text-[12px] text-ink-soft">
          <ScissorsIcon size={14} className="shrink-0" />
          {enCurso ? (
            <span className="truncate">
              Próximo corte:{' '}
              {/* Fecha corta y sin monoespaciada: en 390 px, «12 de octubre»
                  en mono no cabía junto al botón y la línea se cortaba en
                  «12 de octub…», justo en el dato que la línea existe para dar. */}
              <span className="font-semibold text-ink">{formatoCorto(enCurso.cutoffDate)}</span>{' '}
              <span className="text-ink-faint">
                ({enCurso.daysUntilCutoff === 0 ? 'hoy' : `en ${enCurso.daysUntilCutoff} días`})
              </span>
            </span>
          ) : (
            <span className="truncate">Próximo corte: sin calcular</span>
          )}
        </p>

        {/* «Registrar pago», no «Pagar»: PayBae no mueve dinero en el banco.
            Anota el pago que ya hiciste y lo descuenta de la cuenta de origen. */}
        {pagable(card) ? (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onPagar()
            }}
            className="flex min-h-[44px] shrink-0 items-center rounded-pill border border-line bg-surface px-3 text-[13px] font-semibold text-ink active:scale-95"
          >
            Registrar pago
          </button>
        ) : null}
      </footer>
    </Card>
  )
}

/** Hay algo que abonar si existe un ciclo con saldo, cerrado o en curso. */
function pagable(card: CreditCard): boolean {
  return estadoAPagar(card) !== null
}

/**
 * Qué estado de cuenta se abona.
 *
 * El cerrado manda: es el que ya cortó y tiene fecha límite. Solo si no hay
 * ninguno cerrado con saldo se ofrece el ciclo en curso, y entonces el abono es
 * un adelanto —el servidor lo acumula y no marca el ciclo como pagado, porque
 * todavía pueden entrar cargos.
 */
function estadoAPagar(card: CreditCard): StatementSummary | null {
  if (card.closedStatement && card.closedStatement.balanceDueCents > 0) return card.closedStatement
  if (card.openStatement && card.openStatement.balanceDueCents > 0) return card.openStatement
  return card.closedStatement
}

/**
 * Registro del pago de un estado de cuenta.
 *
 * Es la acción que prometía el botón «Pagar» del inicio. Pide lo mínimo que el
 * servidor necesita —cuánto y de qué cuenta sale— porque la fecha la resuelve
 * él en la zona horaria del hogar: tomarla del reloj del teléfono mandaría el
 * pago de las once de la noche al día siguiente.
 */
function HojaPago({ card, onCerrar }: { card: CreditCard; onCerrar: () => void }) {
  const cuentas = useAccounts()
  const pagar = usePagarTarjeta()

  const estado = estadoAPagar(card)
  const [montoCents, setMontoCents] = useState(estado?.balanceDueCents ?? 0)
  const [cuentaId, setCuentaId] = useState('')
  const [intentado, setIntentado] = useState(false)

  // Los vales de despensa son una bolsa cerrada: solo compran despensa. El
  // servidor rechaza pagar con ellos, así que ni se ofrecen.
  const elegibles = (cuentas.data ?? []).filter(
    (c) => !c.archivedAt && c.type !== 'MEAL_VOUCHER',
  )

  const opciones: ReadonlyArray<OpcionSegmento<string>> = elegibles.map((c) => ({
    value: c.id,
    label: c.name,
    detalle: `${formatMoney(c.balanceCents, { hideZeroCents: true })}${
      c.owner ? ` · ${c.owner.user.displayName.split(' ')[0]}` : ' · Común'
    }`,
  }))

  const cuenta = elegibles.find((c) => c.id === cuentaId) ?? null
  const errorMonto = intentado && montoCents <= 0 ? 'Escribe cuánto pagaste.' : undefined
  const errorCuenta = intentado && !cuenta ? 'Elige de qué cuenta salió el dinero.' : undefined

  const enviar = (): void => {
    setIntentado(true)
    if (!estado || montoCents <= 0 || !cuenta) {
      haptic('error')
      return
    }

    pagar.mutate(
      {
        cardId: card.id,
        statementId: estado.id,
        amountCents: montoCents,
        // El servidor lo llama `fromAccountId`; con otro nombre el pago se cae
        // en validación y el dinero nunca se mueve.
        fromAccountId: cuenta.id,
      },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        // Sin limpiar nada: el monto tecleado y la cuenta elegida se conservan
        // para que el segundo intento no cueste ni un toque más.
        onError: () => haptic('error'),
      },
    )
  }

  return (
    <FormSheet
      tinte="lavender"
      titulo={`Pagar ${card.name}`}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={pagar.error ? mensajeDeError(pagar.error) : null} />
          <Guardar onClick={enviar} guardando={pagar.isPending}>
            Registrar pago
          </Guardar>
        </div>
      }
    >
      {estado ? (
        <div className="rounded-card bg-surface-sunken p-3.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex items-center gap-2 text-[13px] text-ink-soft">
              {card.owner ? <PersonDot token={card.owner.personColorToken} /> : null}
              {estado.isClosed ? 'Saldo del corte' : 'Saldo del ciclo en curso'}
            </span>
            <Money cents={estado.balanceDueCents} className="text-lg" />
          </div>
          <p className="mt-1.5 text-[12px] text-ink-soft">
            Corte del <span className="font-mono text-ink">{formatoLargo(estado.cutoffDate)}</span>
            {' · límite '}
            <span className="font-mono text-ink">{formatoLargo(estado.dueDate)}</span>
          </p>
          {!estado.isClosed ? (
            <p className="mt-1 text-[12px] text-ink-faint">
              Este ciclo aún no corta, así que el abono queda como adelanto y la tarjeta sigue
              admitiendo cargos hasta el corte.
            </p>
          ) : null}
        </div>
      ) : null}

      <Campo
        label="¿Cuánto pagaste?"
        ayuda={
          estado && montoCents > estado.balanceDueCents && estado.balanceDueCents > 0
            ? 'Es más de lo que se debe: el excedente queda a favor en la tarjeta.'
            : 'Pagar el total del corte es lo que evita los intereses; el pago mínimo no.'
        }
        error={errorMonto}
      >
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>

      <Campo
        label="¿De qué cuenta salió?"
        ayuda="Se descuenta de esa cuenta y queda registrado como movimiento. Los vales de despensa no aparecen: no pueden pagar una tarjeta."
        error={errorCuenta}
      >
        {cuentas.isPending ? (
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-[52px]" />
            <Skeleton className="h-[52px]" />
          </div>
        ) : opciones.length === 0 ? (
          <EmptyState
            title="No hay cuenta de dónde pagar"
            description="Necesitas al menos una cuenta de débito o efectivo. Los vales de despensa no cuentan."
            icon={<WalletIcon size={26} />}
          />
        ) : (
          <Segmentos opciones={opciones} value={cuentaId} onChange={setCuentaId} />
        )}
      </Campo>

      {cuenta && montoCents > cuenta.balanceCents ? (
        // No lo bloquea: el saldo que guarda la app puede ir atrasado respecto
        // al banco. Avisar y dejar continuar respeta lo que la persona sabe.
        <p className="text-[12px] leading-snug text-expense-text">
          {cuenta.name} quedaría en números rojos: tiene{' '}
          {formatMoney(cuenta.balanceCents, { hideZeroCents: true })}.
        </p>
      ) : null}
    </FormSheet>
  )
}

function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos registrar el pago. Inténtalo de nuevo.'
}

/**
 * Esqueleto de carga.
 *
 * Repite la estructura de la fila real —anillo, tres líneas de texto, bloque de
 * pago y pie— en vez de un rectángulo genérico: así el alto coincide con el del
 * contenido y la pantalla no salta al llegar los datos. Dentro del bloque hundido
 * los rectángulos van en `bg-line`, porque el gris del esqueleto es justo el
 * fondo de ese bloque y no se vería.
 */
function Cargando() {
  return (
    <>
      <ScreenHeader title="Tarjetas" subtitle="Cargando…" tinte="lavender" />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-3 pt-1">
        {[0, 1].map((i) => (
          <Card key={i} className="flex flex-col gap-4">
            <header className="flex items-start gap-3">
              <Skeleton className="size-[52px] rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col">
                <Skeleton className="h-[15px] w-36" />
                <Skeleton className="mt-1 h-[12px] w-24" />
                <Skeleton className="mt-1.5 h-[12px] w-40" />
              </div>
              <Skeleton className="h-[14px] w-16 shrink-0" />
            </header>

            <div className="rounded-card bg-surface-sunken p-3.5">
              <div className="flex items-baseline justify-between gap-3">
                <Skeleton className="h-[16px] w-48 bg-line" />
                <Skeleton className="h-[20px] w-20 bg-line" />
              </div>
              <Skeleton className="mt-1.5 h-[12px] w-52 bg-line" />
            </div>

            <div className="border-t border-line pt-3">
              <Skeleton className="h-[12px] w-56" />
            </div>
          </Card>
        ))}

        <Skeleton className="h-[86px]" />
      </div>
    </>
  )
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

function formatoLargo(date: string): string {
  const [, month, day] = date.split('-')
  return `${Number(day)} de ${MESES[Number(month) - 1]}`
}

/** «12 oct»: para las líneas que comparten renglón con un botón. */
function formatoCorto(date: string): string {
  const [, month, day] = date.split('-')
  return `${Number(day)} ${MESES[Number(month) - 1]!.slice(0, 3)}`
}
